import { Close, Delete, Upload } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";

import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormHelperText,
	IconButton,
	Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";

import { toast } from "react-toastify";

import Lottie from "lottie-react";
import { uploadStatus } from "../../apis/Product";
import groovyWalkAnimation from "../../assets/65364-loader.json";
import excel_icon from "../../assets/excel_icon.png";
import NotificationModal from "../Products/NotificationModal.js";
import ProgressBar from "./ProgressBar.js";

export const UploadDrawer = ({
	setIsUploadDrawerOpen,
	uploadExcel,
	isFromBrand = false,
}) => {
	const {
		register,
		handleSubmit,

		formState: { errors },
	} = useForm();

	const [filename, setFilename] = useState("");
	const [loading, setLoading] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [isUploading, setIsUploading] = useState(false);
	const [taskId, setTaskId] = useState("");
	const [progress, setProgress] = useState(0);
	const [openModal, setOpenModal] = useState(false);
	const [taskStatus, setTaskStatus] = useState("");
	const intervalRef = useRef(null);
	const [progressText, setProgressText] = useState("");

	const handleOnChange = async (event) => {
		const newFile = event.target?.files?.[0];

		if (!newFile) {
			setFilename("");
			setSelectedFile(null);
			setIsUploading(false);
			return;
		}
		setProgressText("");
		setIsUploading(true);
		setFilename(newFile.name);
		setSelectedFile(newFile);

		setTimeout(() => {
			setIsUploading(false);
		}, 1000);
	};

	const handleRemoveFile = async () => {
		setProgressText("");
		setFilename("");
		setSelectedFile(null);
		const fileInput = document.getElementById("file-upload");
		if (fileInput) {
			fileInput.value = "";
		}
	};

	const showError = (errors) => {
		setLoading(false);
		setFilename("");
		setSelectedFile(null);
		const fileInput = document.getElementById("file-upload");
		if (fileInput) {
			fileInput.value = "";
		}
		toast.error(
			<Box sx={{ textAlign: "left", padding: "20px" }}>
				<Typography variant="h6" sx={{ marginBottom: 2 }}>
					📊 Product Upload Summary
				</Typography>
				{errors.map((item, index) => {
					return (
						<Box
							key={`error-${index}`}
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								paddingY: "10px",
							}}
						>
							<Typography
								variant="body2"
								sx={{ fontWeight: 500 }}
							>
								{item}
							</Typography>
						</Box>
					);
				})}
			</Box>,

			{
				position: "bottom-center",
				autoClose: false,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				style: {
					maxHeight: "300px",
					overflowY: "scroll",
					minWidth: "600px", // Adjust the width of the notification box
					maxWidth: "600px", // Set a maximum width if needed
					padding: "20px", //
				},
			},
			2
		);
	};

	const showSuccess = (data) => {
		setLoading(false);
		setFilename("");
		setSelectedFile(null);
		const fileInput = document.getElementById("file-upload");
		if (fileInput) {
			fileInput.value = "";
		}
		toast.success(
			<Box sx={{ textAlign: "left", padding: "20px" }}>
				<Typography variant="h6" sx={{ marginBottom: 2 }}>
					📊 Product Upload Summary
				</Typography>

				{/* Total Uploaded/Updated */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: 2,
					}}
				>
					<Typography variant="body1" sx={{ fontWeight: 500 }}>
						Total Uploaded/Updated:
					</Typography>
					<Typography variant="h6" sx={{ fontWeight: 500 }}>
						{data.successful_count}
					</Typography>
				</Box>

				{/* Uploaded New Products */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: 2,
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						Uploaded New Products:
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{data.added_new_product_count}
					</Typography>
				</Box>

				{/* Updated Existing Products */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: 2,
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						Updated Existing Products:
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{data.edited_existing_products_count}
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginBottom: 2,
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						No Changes in Products:
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{data.no_change_in_product_count}
					</Typography>
				</Box>

				{/* Duplicate Rows in Sheet */}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						Duplicate Rows Count:
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{data.duplicate_count}
					</Typography>
				</Box>
				{data.duplicate_rows.length > 0 && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginTop: 2,
						}}
					>
						<Typography
							variant="body2"
							sx={{ fontWeight: 500, width: "50%" }}
						>
							Duplicate Rows are:
						</Typography>
						<Typography
							variant="body2"
							sx={{ fontWeight: 500, width: "50%" }}
						>
							{JSON.stringify(data.duplicate_rows)}
						</Typography>
					</Box>
				)}
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginTop: 2,
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{`Skipped Product due to Rating < 4 :`}
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{JSON.stringify(data.skipped_product_due_to_rating)}
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginTop: 2,
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{`Skipped Product due to Price > Rs.4000 :`}
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{JSON.stringify(data.skipped_product_due_to_price)}
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						marginTop: 2,
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						Total Rows:
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 500 }}>
						{JSON.stringify(
							data.skipped_product_due_to_price +
							data.skipped_product_due_to_rating +
							data.duplicate_count +
							data.successful_count
						)}
					</Typography>
				</Box>
			</Box>,
			{
				position: "bottom-center",
				autoClose: false,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				style: {
					maxHeight: "800px",
					overflowY: "scroll",
					minWidth: "600px", // Adjust the width of the notification box
					maxWidth: "600px", // Set a maximum width if needed
					padding: "20px", // Adjust padding for more spacing
				},
			}
		);
	};

	const onSubmit = async () => {
		setProgressText("");
		setLoading(true);

		const formData = new FormData();

		if (!selectedFile) {
			console.error("No file selected!");
			setLoading(false);
			return;
		}

		formData.append("file", selectedFile);

		console.log("Final FormData:", formData);

		const res = await uploadExcel(formData);

		if (isFromBrand) {
			if (res.success) {
				setProgress(100);

				showSuccess(res.data);
				setIsUploadDrawerOpen(false);
			} else {
				showError(res.error.errors);
			}
		} else {
			if (res.data.status === "success") {
				setTaskId(res.data.task_id);
			} else {
				setLoading(false);
				console.log("RESPONSE", res);
				showError(res.data.errors);
			}
		}
	};

	const clearPolling = () => {
		console.log("Stopping polling...");
		clearInterval(intervalRef.current);
	};

	useEffect(() => {
		if (taskId) {
			console.log("Starting polling for Task ID:", taskId);
			clearInterval(intervalRef.current);

			intervalRef.current = setInterval(async () => {
				const response = await uploadStatus(taskId);
				setTaskStatus(response.data.status);

				if (response.data.status === "pending") {
					setProgressText("Pending...");
				}
				if (response.data.status === "validating") {
					setProgressText("Validating...");
				}
				if (response.data.status === "in_progress") {
					if (response?.data?.progress) {
						setProgress(response.data.progress);
						setProgressText("In Progress...");
					}
				} else if (response.data.status === "success") {
					setProgress(100);
					clearInterval(intervalRef.current);
					setIsUploadDrawerOpen(false);
					showSuccess(response.data.data);
					setProgressText("Succeeded");
				} else {
					console.log("Task failed:", response.data.data.errors);
					clearInterval(intervalRef.current);
					showError(response.data.data.errors);
					setProgressText("Failed");
				}
			}, 1000);

			return () => clearPolling();
		}
	}, [setIsUploadDrawerOpen, taskId]);

	const handleCloseDrawer = async () => {
		if (taskStatus === "pending" || taskStatus === "in_progress") {
			setOpenModal(true);
		} else {
			setIsUploadDrawerOpen(false);
		}
	};

	return (
		<div
			style={{
				width: 600,
				padding: 5,
				height: "100vh",
				overflowY: "scroll",
			}}
		>
			<IconButton
				style={{ position: "fixed" }}
				onClick={handleCloseDrawer}
			>
				<Close />
			</IconButton>
			{/* {loading && <Loader />} */}
			{loading && (
				<Box
					style={{
						width: "100%",
						height: "100%",
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Box>
						<Lottie
							style={{ width: "200px" }}
							animationData={groovyWalkAnimation}
						></Lottie>
					</Box>
					<Typography style={{ marginBottom: "20px" }}>
						{progressText}
					</Typography>
					<Box style={{ width: "60%" }}>
						<ProgressBar value={progress}></ProgressBar>
					</Box>
				</Box>
			)}

			{/* <LinearWithValueLabel progress={progress}></LinearWithValueLabel>
      <LinearProgressWithLabel value={progress} /> */}

			{!loading && (
				<div style={{ padding: "45px 55px 0px", widows: "100%" }}>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							handleSubmit(onSubmit)();
						}}
						style={{ width: "100%" }}
					>
						<Box my={3}>
							<Typography variant="h6" gutterBottom>
								Uplaod Sheet
							</Typography>
							<FormControl error={Boolean(errors.file)}>
								<Box
									style={{
										width: "300px",
										height: "300px",
										borderRadius: "10px",
										border: "1px solid black",
										padding: 20,
										display: "flex",
										flexDirection: "row",
										alignItems: "center",
										justifyContent: "center",
										position: "relative",
									}}
								>
									{isUploading ? (
										<CircularProgress /> // Loading animation
									) : filename ? (
										<Box>
											<IconButton
												style={{
													position: "absolute",
													top: 0,
													right: 0,
												}}
												onClick={(e) => {
													e.stopPropagation(); // Prevents opening file dialog when deleting
													handleRemoveFile();
												}}
											>
												<Delete />
											</IconButton>
											<img
												src={excel_icon}
												style={{
													width: 80,
													height: 80,
												}}
												alt="Excel Icon"
											/>
										</Box>
									) : (
										<Box
											style={{
												display: "flex",
												flexDirection: "column",
												alignItems: "center",
											}}
										>
											<Upload
												style={{
													fontSize: 40,
													marginBottom: 8,
												}}
											/>
											<Typography
												style={{ textAlign: "center" }}
												variant="h6"
											>
												Select Sheet and upload
											</Typography>
										</Box>
									)}
								</Box>
								{filename && (
									<Typography
										style={{
											marginTop: 16,
											overflowWrap: "break-word",
											wordBreak: "break-all",
											maxWidth: "300px", // Ensure it doesn't exceed container width
											whiteSpace: "normal", // Allow text to wrap
										}}
									>
										File Name: {filename}
									</Typography>
								)}
								<Button
									variant="contained"
									component="label"
									style={{ marginTop: 30 }}
									color="secondary"
								>
									Select Sheet
									<input
										hidden
										accept=".xlsx,.csv"
										id="file-upload"
										type="file"
										{...register("file", {
											required: "Sheet is required",
										})} // Add validation
										onChange={handleOnChange} // Move onChange here
									/>
								</Button>

								{errors.file && (
									<FormHelperText>
										{errors.file.message}
									</FormHelperText>
								)}

								<Box
									display="flex"
									justifyContent="space-between"
									style={{ marginTop: 20, width: "100%" }}
								>
									<Button
										style={{ width: "100%" }}
										type="submit"
										color="primary"
										variant="contained"
									>
										Submit
									</Button>
								</Box>
							</FormControl>
						</Box>
					</form>
				</div>
			)}
			<NotificationModal
				open={openModal}
				setOpen={setOpenModal}
				taskId={taskId}
				closeDrawer={setIsUploadDrawerOpen}
				clearPolling={clearPolling}
			></NotificationModal>
		</div>
	);
};
